import { BackgroundJobStateFilter } from 'src/api/FiretrolApiClient';
import { AssumedScope, currentScope } from './queryScope';

const ROOT = 'ft' as const;

// Every key is rooted at the currently-assumed identity so impersonation
// gets a physically separate cache subtree (see queryScope.ts).
const scoped = <T extends readonly unknown[]>(
  parts: T,
  scope: AssumedScope = currentScope(),
) => [ROOT, scope, ...parts] as const;

// Keys are hierarchical: invalidating a parent key refetches every active
// query beneath it. Per-entity subtrees (`xxx.forJob(jobId)`, `xxx.detail(id)`)
// exist so a write can refresh one job's or one record's data without
// touching the rest of the domain. Write sites never build keys inline.
export const queryKeys = {
  all: (scope?: AssumedScope) => scoped([], scope),

  users: {
    all: () => scoped(['users']),
    list: () => scoped(['users', 'list']),
    detail: (id: number) => scoped(['users', 'detail', id]),
    me: () => scoped(['users', 'me']),
    logins: (userId: number) => scoped(['users', 'logins', userId]),
    // Org-wide login history, paged by archive number — a different endpoint
    // from `logins(userId)` above, so it gets its own subtree.
    loginArchive: (archive: number) =>
      scoped(['users', 'loginArchive', archive]),
    search: (query: string) => scoped(['users', 'search', query]),
  },

  jobs: {
    all: () => scoped(['jobs']),
    list: () => scoped(['jobs', 'list']),
    closedList: () => scoped(['jobs', 'closed']),
    search: (query: string) => scoped(['jobs', 'search', query]),
    detail: (id: number) => scoped(['jobs', 'detail', id]),
    summary: (id: number) => scoped(['jobs', 'detail', id, 'summary']),
    team: (id: number) => scoped(['jobs', 'detail', id, 'team']),
    users: (id: number) => scoped(['jobs', 'detail', id, 'users']),
    meetingTypes: (id: number) =>
      scoped(['jobs', 'detail', id, 'meetingTypes']),
    hyperlinks: (id: number) => scoped(['jobs', 'detail', id, 'hyperlinks']),
    templates: (id: number) => scoped(['jobs', 'detail', id, 'templates']),
    teamByOffice: (officeName: string) =>
      scoped(['jobs', 'teamByOffice', officeName]),
    teamByRoles: (id: number, roleIds: readonly number[]) =>
      scoped(['jobs', 'detail', id, 'team', 'byRoles', roleIds]),
  },

  organization: {
    all: () => scoped(['organization']),
    adminUsers: (includeArchived = false) =>
      scoped([
        'organization',
        'adminUsers',
        ...(includeArchived ? ['includeArchived'] : []),
      ]),
    adminUserDetail: (id: number) =>
      scoped(['organization', 'adminUsers', 'detail', id]),
    roles: () => scoped(['organization', 'roles']),
    roleDetail: (id: number) => scoped(['organization', 'roles', 'detail', id]),
    offices: () => scoped(['organization', 'offices']),
    officeDetail: (id: number) =>
      scoped(['organization', 'offices', 'detail', id]),
    officeByDivision: (divisionId: number) =>
      scoped(['organization', 'offices', 'byDivision', divisionId]),
    changeTemplates: () => scoped(['organization', 'changeTemplates']),
    changeTemplateDetail: (id: string) =>
      scoped(['organization', 'changeTemplates', 'detail', id]),
    divisions: () => scoped(['organization', 'divisions']),
    contacts: () => scoped(['organization', 'contacts']),
    contactsByCustomer: (customerNumber: string) =>
      scoped(['organization', 'contacts', 'byCustomer', customerNumber]),
    clients: () => scoped(['organization', 'clients']),
    clientSearch: (query: string) =>
      scoped(['organization', 'clients', 'search', query]),
    summaries: () => scoped(['organization', 'summaries']),
    apiConnections: () => scoped(['organization', 'apiConnections']),
  },

  costCodes: { list: () => scoped(['costCodes', 'list']) },
  payCodes: { list: () => scoped(['payCodes', 'list']) },
  classifications: { list: () => scoped(['classifications', 'list']) },
  userRates: { list: () => scoped(['userRates', 'list']) },
  unitPricing: { list: () => scoped(['unitPricing', 'list']) },
  changeTypes: {
    all: () => scoped(['changeTypes']),
    list: () => scoped(['changeTypes', 'list']),
    detail: (id: string) => scoped(['changeTypes', 'detail', id]),
  },

  documentTemplates: {
    all: () => scoped(['documentTemplates']),
    list: () => scoped(['documentTemplates', 'list']),
    detail: (id: string) => scoped(['documentTemplates', 'detail', id]),
  },
  milestoneTemplates: {
    all: () => scoped(['milestoneTemplates']),
    list: () => scoped(['milestoneTemplates', 'list']),
    detail: (id: string) => scoped(['milestoneTemplates', 'detail', id]),
  },
  milestonePhases: {
    all: () => scoped(['milestonePhases']),
    forJob: (jobId: number) => scoped(['milestonePhases', 'job', jobId]),
    forTemplate: (templateId: string) =>
      scoped(['milestonePhases', 'template', templateId]),
  },
  milestoneItems: {
    all: () => scoped(['milestoneItems']),
    forJob: (jobId: number) => scoped(['milestoneItems', 'job', jobId]),
    forPhase: (jobId: number, phaseId: string | null) =>
      scoped(['milestoneItems', 'job', jobId, 'phase', phaseId ?? 'all']),
    links: (jobId: number, itemId: string) =>
      scoped(['milestoneItems', 'job', jobId, 'links', itemId]),
  },
  dailyProgress: {
    history: (jobId: number) => scoped(['dailyProgress', 'history', jobId]),
  },

  changes: {
    all: () => scoped(['changes']),
    overview: () => scoped(['changes', 'overview']),
    forJob: (jobId: number) => scoped(['changes', 'job', jobId]),
    list: (jobId: number) => scoped(['changes', 'job', jobId, 'list']),
    detail: (jobId: number, id: string) =>
      scoped(['changes', 'job', jobId, 'detail', id]),
    documentOptions: (jobId: number) =>
      scoped(['changes', 'job', jobId, 'documentOptions']),
  },
  rfis: {
    all: () => scoped(['rfis']),
    overview: () => scoped(['rfis', 'overview']),
    forJob: (jobId: number) => scoped(['rfis', 'job', jobId]),
    list: (jobId: number) => scoped(['rfis', 'job', jobId, 'list']),
    detail: (jobId: number, id: string) =>
      scoped(['rfis', 'job', jobId, 'detail', id]),
    documentOptions: (jobId: number) =>
      scoped(['rfis', 'job', jobId, 'documentOptions']),
  },
  submittals: {
    all: () => scoped(['submittals']),
    forJob: (jobId: number) => scoped(['submittals', 'job', jobId]),
    list: (jobId: number) => scoped(['submittals', 'job', jobId, 'list']),
    detail: (jobId: number, id: string) =>
      scoped(['submittals', 'job', jobId, 'detail', id]),
    documentOptions: (jobId: number) =>
      scoped(['submittals', 'job', jobId, 'documentOptions']),
  },
  drawings: {
    all: () => scoped(['drawings']),
    forJob: (jobId: number) => scoped(['drawings', 'job', jobId]),
    list: (jobId: number) => scoped(['drawings', 'job', jobId, 'list']),
    detail: (jobId: number, id: string) =>
      scoped(['drawings', 'job', jobId, 'detail', id]),
    documentOptions: (jobId: number) =>
      scoped(['drawings', 'job', jobId, 'documentOptions']),
  },
  transmittals: {
    all: () => scoped(['transmittals']),
    forJob: (jobId: number) => scoped(['transmittals', 'job', jobId]),
    list: (jobId: number) => scoped(['transmittals', 'job', jobId, 'list']),
    detail: (jobId: number, id: string) =>
      scoped(['transmittals', 'job', jobId, 'detail', id]),
    documentOptions: (jobId: number) =>
      scoped(['transmittals', 'job', jobId, 'documentOptions']),
  },
  inspections: {
    overview: () => scoped(['inspections', 'overview']),
  },

  workflows: {
    all: () => scoped(['workflows']),
    templates: () => scoped(['workflows', 'templates']),
    templateDetail: (id: string) =>
      scoped(['workflows', 'templates', 'detail', id]),
    templateByType: (resourceId: string) =>
      scoped(['workflows', 'templates', 'byType', resourceId]),
    forJob: (jobId: number) => scoped(['workflows', 'job', jobId]),
    forDivision: (divisionId: number) =>
      scoped(['workflows', 'division', divisionId]),
    optionsByDivision: (divisionId: number, resourceId: string) =>
      scoped(['workflows', 'division', divisionId, 'options', resourceId]),
    byOffice: (officeId: number, resourceId: string) =>
      scoped(['workflows', 'office', officeId, 'workflow', resourceId]),
    optionsByOffice: (officeId: number, resourceId: string) =>
      scoped(['workflows', 'office', officeId, 'options', resourceId]),
  },
  workflowNotifications: {
    all: () => scoped(['workflowNotifications']),
    history: (resourceType: string, jobId: number, entityId: string) =>
      scoped(['workflowNotifications', resourceType, jobId, entityId]),
  },
  emails: {
    all: () => scoped(['emails']),
    forJob: (jobNumber: string) => scoped(['emails', 'job', jobNumber]),
    forRecord: (
      jobNumber: string,
      productType: string,
      productNumber: string,
    ) => scoped(['emails', 'job', jobNumber, productType, productNumber]),
  },

  attachments: {
    url: (id: string) => scoped(['attachments', 'url', id]),
  },

  documents: {
    all: () => scoped(['documents']),
    forJob: (jobId: number) => scoped(['documents', 'job', jobId]),
    folders: (jobId: number) => scoped(['documents', 'job', jobId, 'folders']),
    list: (jobId: number) => scoped(['documents', 'job', jobId, 'list']),
    inFolder: (jobId: number, folderId: string) =>
      scoped(['documents', 'job', jobId, 'folder', folderId]),
    detail: (jobId: number, documentId: string) =>
      scoped(['documents', 'job', jobId, 'detail', documentId]),
  },

  posts: {
    all: () => scoped(['posts']),
    activityFeed: () => scoped(['posts', 'activityFeed']),
    allAssigned: () => scoped(['posts', 'allAssigned']),
    forJob: (jobId: number) => scoped(['posts', 'job', jobId]),
    search: (jobId: number, params: object) =>
      scoped(['posts', 'job', jobId, 'search', params]),
    assigned: (jobId: number) => scoped(['posts', 'job', jobId, 'assigned']),
    detail: (jobId: number, postId: string) =>
      scoped(['posts', 'job', jobId, 'detail', postId]),
    comments: (postId: string) => scoped(['posts', 'comments', postId]),
    documentOptions: (jobId: number, query: string) =>
      scoped(['posts', 'job', jobId, 'documentOptions', query]),
    linkOptions: (jobId: number, query: string) =>
      scoped(['posts', 'job', jobId, 'linkOptions', query]),
  },
  userNotifications: {
    all: () => scoped(['userNotifications']),
    list: (filter: object) => scoped(['userNotifications', 'list', filter]),
  },

  meetings: {
    all: () => scoped(['meetings']),
    forJob: (jobId: number) => scoped(['meetings', 'job', jobId]),
    list: (jobId: number) => scoped(['meetings', 'job', jobId, 'list']),
    detail: (jobId: number, meetingId: string) =>
      scoped(['meetings', 'job', jobId, 'detail', meetingId]),
    // Under the meeting's detail key, so every edit that refreshes the
    // meeting also refreshes its rendered PDF.
    pdf: (jobId: number, meetingId: string) =>
      scoped(['meetings', 'job', jobId, 'detail', meetingId, 'pdf']),
    issues: (jobId: number) => scoped(['meetings', 'job', jobId, 'issues']),
    comments: (issueId: string) =>
      scoped(['meetings', 'issueComments', issueId]),
  },
  questions: {
    forJob: (jobId: number) => scoped(['questions', 'job', jobId]),
  },

  timesheets: {
    all: () => scoped(['timesheets']),
    detailAll: () => scoped(['timesheets', 'detail']),
    detail: (jobId: number, userId: number, year: number, weekId: number) =>
      scoped(['timesheets', 'detail', jobId, userId, year, weekId]),
    forJobAll: () => scoped(['timesheets', 'job']),
    forJob: (jobId: number, params: object) =>
      scoped(['timesheets', 'job', jobId, params]),
    adminListAll: () => scoped(['timesheets', 'admin']),
    adminList: (officeId: number, params: object) =>
      scoped(['timesheets', 'admin', officeId, params]),
    summaryAll: () => scoped(['timesheets', 'summary']),
    summary: (officeId: number, params: object) =>
      scoped(['timesheets', 'summary', officeId, params]),
    missingAll: () => scoped(['timesheets', 'missing']),
    missing: (officeId: number, params: object) =>
      scoped(['timesheets', 'missing', officeId, params]),
    byId: (timesheetId: string) =>
      scoped(['timesheets', 'detail', 'byId', timesheetId]),
    usersForOfficeAll: () => scoped(['timesheets', 'usersForOffice']),
    usersForOffice: (officeId: number, onlyMyTeam: boolean) =>
      scoped(['timesheets', 'usersForOffice', officeId, onlyMyTeam]),
    auditReportAll: () => scoped(['timesheets', 'auditReport']),
    auditReport: (params: object) =>
      scoped(['timesheets', 'auditReport', params]),
    payrollReportAll: () => scoped(['timesheets', 'payrollReport']),
    payrollReport: (params: object) =>
      scoped(['timesheets', 'payrollReport', params]),
    external: (token: string, id: string) =>
      scoped(['timesheets', 'external', token, id]),
    externalBatch: (token: string) =>
      scoped(['timesheets', 'externalBatch', token]),
    weekHoursAll: () => scoped(['timesheets', 'weekHours']),
    weekHours: (userId: number, year: number, weekId: number) =>
      scoped(['timesheets', 'weekHours', userId, year, weekId]),
    effectiveRate: (params: object) =>
      scoped(['timesheets', 'effectiveRate', params]),
  },
  dailys: {
    all: () => scoped(['dailys']),
    list: (params: object) => scoped(['dailys', 'list', params]),
    detail: (id: string) => scoped(['dailys', 'detail', id]),
    submissionReport: (params: object) =>
      scoped(['dailys', 'submissionReport', params]),
    designProgressSummaryForJob: (jobId: number) =>
      scoped(['dailys', 'designProgressSummary', jobId]),
    designProgressSummary: (jobId: number, params: object) =>
      scoped(['dailys', 'designProgressSummary', jobId, params]),
  },
  workTickets: {
    all: () => scoped(['workTickets']),
    forJob: (jobId: number) => scoped(['workTickets', 'job', jobId]),
    detail: (jobId: number, ticketId: string) =>
      scoped(['workTickets', 'job', jobId, 'detail', ticketId]),
  },
  rates: {
    forJob: (jobId: number) => scoped(['rates', 'job', jobId]),
  },
  manhours: {
    filters: () => scoped(['manhours', 'filters']),
    summary: (params: object) => scoped(['manhours', 'summary', params]),
  },
  serviceJobs: {
    all: () => scoped(['serviceJobs']),
    list: (params: object) => scoped(['serviceJobs', 'list', params]),
  },
  serviceWorkOrders: {
    search: (term: string) => scoped(['serviceWorkOrders', 'search', term]),
  },

  wbs: {
    all: () => scoped(['wbs']),
    forJob: (jobId: number) => scoped(['wbs', 'job', jobId]),
    adjustments: (accountId: string) =>
      scoped(['wbs', 'account', accountId, 'adjustments']),
    adjustmentTypes: () => scoped(['wbs', 'adjustmentTypes']),
  },
  designModels: {
    all: () => scoped(['designModels']),
    list: (jobId: number) => scoped(['designModels', 'list', jobId]),
    detail: (id: string) => scoped(['designModels', 'detail', id]),
    ifc: (id: string) => scoped(['designModels', 'ifc', id]),
    grouping: (id: string) => scoped(['designModels', 'grouping', id]),
    laborAccounts: (jobId: number) =>
      scoped(['designModels', 'laborAccounts', jobId]),
  },
  ctc: {
    all: () => scoped(['ctc']),
    list: (divisions: readonly string[]) => scoped(['ctc', 'list', divisions]),
    closed: () => scoped(['ctc', 'closed']),
    linkedJobs: () => scoped(['ctc', 'linkedJobs']),
    currentMonthStatus: () => scoped(['ctc', 'currentMonthStatus']),
    job: (id: string) => scoped(['ctc', 'job', id]),
    adjustments: (id: string) => scoped(['ctc', 'job', id, 'adjustments']),
    adjustmentHistory: (id: string) =>
      scoped(['ctc', 'job', id, 'adjustmentHistory']),
    navigation: (id: string, divisions: readonly string[]) =>
      scoped(['ctc', 'job', id, 'navigation', divisions]),
    materials: (id: string) => scoped(['ctc', 'job', id, 'materials']),
    materialsTotal: (id: string) =>
      scoped(['ctc', 'job', id, 'materialsTotal']),
    materialsForCode: (id: string, costCode: string) =>
      scoped(['ctc', 'job', id, 'materialsForCode', costCode]),
    breakdown: (id: string, costCode: string) =>
      scoped(['ctc', 'job', id, 'breakdown', costCode]),
  },

  knowledgeArticles: {
    all: () => scoped(['knowledgeArticles']),
    list: (filter: object) => scoped(['knowledgeArticles', 'list', filter]),
    detail: (id: number) => scoped(['knowledgeArticles', 'detail', id]),
  },

  chat: {
    all: () => scoped(['chat']),
    conversations: () => scoped(['chat', 'conversations']),
    conversation: (conversationId: string) =>
      scoped(['chat', 'conversation', conversationId]),
    messages: (conversationId: string) =>
      scoped(['chat', 'messages', conversationId]),
    usage: () => scoped(['chat', 'usage']),
    memory: () => scoped(['chat', 'memory']),
    queryExport: (auditId: string) => scoped(['chat', 'queryExport', auditId]),
  },
  chatAdmin: {
    all: () => scoped(['chatAdmin']),
    conversations: (params: object) =>
      scoped(['chatAdmin', 'conversations', params]),
    conversationDetail: (id: string) =>
      scoped(['chatAdmin', 'conversationDetail', id]),
    feedback: (params: object) => scoped(['chatAdmin', 'feedback', params]),
    sqlAudit: (params: object) => scoped(['chatAdmin', 'sqlAudit', params]),
    stats: (params: object) => scoped(['chatAdmin', 'stats', params]),
    usage: (params: object) => scoped(['chatAdmin', 'usage', params]),
    userMemory: (userId: number) => scoped(['chatAdmin', 'userMemory', userId]),
  },
  applogs: {
    query: (params: object) => scoped(['applogs', 'query', params]),
  },
  azureGroupSync: {
    all: () => scoped(['azureGroupSync']),
    mappings: () => scoped(['azureGroupSync', 'mappings']),
    status: () => scoped(['azureGroupSync', 'status']),
    logs: (page: number) => scoped(['azureGroupSync', 'logs', page]),
    memberships: () => scoped(['azureGroupSync', 'memberships']),
  },
  backgroundJobs: {
    all: () => scoped(['backgroundJobs']),
    overview: () => scoped(['backgroundJobs', 'overview']),
    servers: () => scoped(['backgroundJobs', 'servers']),
    // The paged lists and the per-job details share the `jobs` prefix so a
    // write can refresh both without touching servers or recurring schedules.
    jobsAll: () => scoped(['backgroundJobs', 'jobs']),
    jobs: (state: BackgroundJobStateFilter, page: number, pageSize: number) =>
      scoped(['backgroundJobs', 'jobs', state, page, pageSize]),
    job: (id: string) => scoped(['backgroundJobs', 'jobs', 'detail', id]),
    recurring: () => scoped(['backgroundJobs', 'recurring']),
  },
  scim: {
    all: () => scoped(['scim']),
    integrations: () => scoped(['scim', 'integrations']),
    logs: (integrationId: string, page: number) =>
      scoped(['scim', 'logs', integrationId, page]),
    logDetail: (integrationId: string, logId: string) =>
      scoped(['scim', 'logDetail', integrationId, logId]),
    userMappings: (integrationId: string) =>
      scoped(['scim', 'userMappings', integrationId]),
  },
  proact: { settings: () => scoped(['proact', 'settings']) },
  healthcheck: { dbStatus: () => scoped(['healthcheck', 'dbStatus']) },
} as const;
